import {
  Box,
  CircularProgress,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import ArrowUpRightIcon from "assets/svg/ArrowUpRightIcon";
import CircleWarningIcon from "assets/svg/CircleWarningIcon";
import DatabaseCopyIcon from "assets/svg/DatabaseCopyIcon";
import PersonIcon from "assets/svg/PersonIcon";
import SendToMobileIcon from "assets/svg/SendToMobileIcon";
import { AxiosError } from "axios";
import PrimaryButton from "components/PrimaryButton/PrimaryButton";
import VerifyTwoFA from "components/VerifyTwoFA/VerifyTwoFA";
import { commonStyle } from "pages/Dashboard/common.styles";
import { useState } from "react";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { TransferRequest } from "reduxStore/dataTypes";
import { RootState } from "reduxStore/store";
import { makeTransferRequest } from "services/apiService";
import theme, { responsiveFontSize } from "themes/Theme";

const SendCoin = () => {
  const [receiverAddress, setReceiverAddress] = useState<string | null>(null);
  const [receiverAddressError, setReceiverAddressError] = useState<
    string | null
  >(null);
  const [amount, setAmount] = useState<string | null>(null);
  const [amountError, setAmountError] = useState<string | null>(null);
  const [verifyTwoFAModal, setVerifyTwoFAModal] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const { user, loading: userLoading } = useSelector(
    (state: RootState) => state.user
  );

  const handleTransfer = () => {
    if (
      receiverAddress === null ||
      receiverAddress === "" ||
      receiverAddress === undefined
    ) {
      setReceiverAddressError("Receiver Address is required");
    } else if (amount === null || amount === "" || amount === undefined) {
      setAmountError("Amount is required");
    } else if (user?.data?.personal_details?.twoFactorEnabled !== 0) {
      setLoading(true);
      setVerifyTwoFAModal(true);
    } else {
      handleTransferRequest("000000");
    }
  };

  const handleTransferRequest = async (token: string) => {
    try {
      setLoading(true);
      const body: TransferRequest = {
        receiverAddress: receiverAddress || "",
        amount: amount || "",
        token: token || "",
      };
      const response = await makeTransferRequest(body);
      if (response && response.status === 200) {
        toast.success(response?.data?.message);
      }
      setLoading(false);
    } catch (error) {
      setLoading(false);
      if (error instanceof AxiosError) {
        toast.error(error?.response?.data?.message);
      }
    } finally {
      setReceiverAddress("");
      setAmount("");
      setLoading(false);
    }
  };

  return (
    <Box sx={commonStyle.cardStyle}>
      <Stack direction={"row"} alignItems={"center"} gap={1}>
        <SendToMobileIcon />
        <Typography sx={commonStyle.cardTitleText}>Send Coin</Typography>
      </Stack>

      <Stack direction={"row"} alignItems={"center"} gap={1} mt={3} mb={1}>
        <PersonIcon />
        <Typography sx={styles.inputLabelStyle}>Receiver Address</Typography>
      </Stack>
      <TextField
        variant="outlined"
        fullWidth
        value={receiverAddress}
        onChange={(e) => {
          if (e.target.value) {
            setReceiverAddressError(null);
          }
          setReceiverAddress(e.target.value);
        }}
        placeholder="Enter receiver address"
        slotProps={styles.inputSlotTextStyle}
        error={!!receiverAddressError}
        helperText={receiverAddressError || ""}
      />
      <Stack direction={"row"} alignItems={"center"} gap={0.5} my={1}>
        <CircleWarningIcon />
        <Typography sx={styles.hintText}>
          enter the exact address that you want to send.
        </Typography>
      </Stack>

      <Stack direction={"row"} alignItems={"center"} gap={1} mt={3} mb={1}>
        <DatabaseCopyIcon />
        <Typography sx={styles.inputLabelStyle}>Amount</Typography>
      </Stack>
      <TextField
        variant="outlined"
        fullWidth
        value={amount}
        onChange={(e) => {
          if (e.target.value) {
            setAmountError(null);
          }
          setAmount(e.target.value);
        }}
        placeholder="Enter amount"
        slotProps={styles.inputSlotTextStyle}
        error={!!amountError}
        helperText={amountError || ""}
      />

      <Stack direction={"row"} alignItems={"center"} gap={0.5} my={1}>
        <CircleWarningIcon />
        <Typography sx={styles.hintText}>
          enter the exact amount that you want to send.
        </Typography>
      </Stack>
      <Box sx={commonStyle.flexSpaceBetween} my={2}>
        <Box>
          <Typography>Total amount</Typography>
          <Typography>
            {amount || "0.00"} <span>ERC</span>
          </Typography>
        </Box>

        {loading ? (
          <CircularProgress />
        ) : (
          <PrimaryButton
            title="Transfer"
            onClick={handleTransfer}
            rightIcon={<ArrowUpRightIcon />}
            viewStyle={{
              borderRadius: "999px",
              padding: "10px 24px",
              background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.light} 100%)`,
              color: "#fff",
              fontWeight: 600,
              fontSize: responsiveFontSize(14),
              boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.15)",
              display: "flex",
              alignItems: "center",
              gap: 1,
              transition: "all 0.2s ease-in-out",
              "&:hover": {
                transform: "translateY(-2px)",
                boxShadow: "0px 6px 16px rgba(0, 0, 0, 0.2)",
                background: `linear-gradient(135deg, ${theme.palette.primary.light} 0%, ${theme.palette.primary.main} 100%)`,
              },
            }}
          />
        )}
      </Box>

      {verifyTwoFAModal && (
        <VerifyTwoFA
          open={verifyTwoFAModal}
          handleClose={() => {
            setVerifyTwoFAModal(false);
            setLoading(false);
          }}
          onSuccess={(token: string) => {
            setVerifyTwoFAModal(false);
            handleTransferRequest(token);
          }}
        />
      )}
    </Box>
  );
};
export default SendCoin;
const styles = {
  inputLabelStyle: {
    fontSize: responsiveFontSize(14),
    fontWeight: 400,
    color: theme.palette.primary.main,
  },
  inputSlotTextStyle: {
    input: {
      sx: {
        fontSize: "0.875rem",
        color: theme.palette.text.primary,
        fontWeight: 400,
      },
    },
  },
  hintText: {
    fontSize: responsiveFontSize(11),
    fontWeight: 400,
    color: theme.palette.text.secondary,
  },
};
